// @ts-nocheck
/**
 * Product card HTML builder for custom-product-list (Storefront API).
 * Usage: pass normalized product data from GraphQL + product JSON to buildProductCardHTML.
 */
(function (global) {
  "use strict";

  function decodeId(base64Id) {
    if (!base64Id) return "";
    if (base64Id.includes("gid://")) return base64Id.split("/").pop();
    try {
      const decoded = atob(base64Id);
      if (decoded.includes("gid://")) return decoded.split("/").pop();
      return decoded;
    } catch (e) {
      return base64Id;
    }
  }

  function normalizeInventoryQuantity(value) {
    if (value == null || value === "") return null;
    const n = Number(value);
    if (!Number.isFinite(n)) return null;
    return n < 0 ? Math.abs(n) : n;
  }

  /**
   * Format price for display: no decimals, comma as thousands separator (e.g. 1047.00 -> "1,047").
   * @param {number|string} amount - Price amount
   * @returns {string} Formatted price string
   */
  function formatPrice(amount) {
    const num = typeof amount === "string" ? parseFloat(amount) : Number(amount);
    if (isNaN(num)) return amount != null ? String(amount) : "";
    const intPart = Math.round(num);
    return intPart.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }

  function resolveUrgencyLabel(tagsLower) {
    const tags = tagsLower || [];
    const hasTag = function (key) {
      return tags.some(function (t) {
        return t.indexOf(key) !== -1;
      });
    };
    if (hasTag("selling-fast")) return { emoji: "🔥", label: "SELLING FAST" };
    if (hasTag("limited-stock")) return { emoji: "⚡", label: "LIMITED STOCK" };
    if (hasTag("lowest-price")) return { emoji: "🤑", label: "LOWEST PRICE" };
    if (hasTag("hurry-up")) return { emoji: "⏳", label: "HURRY UP" };
    if (hasTag("ultimate-deal")) return { emoji: "💎", label: "ULTIMATE DEAL" };
    return null;
  }

  /** True when a Storefront metafield has a real reference / list payload (not empty). */
  function hasMetafieldPayload(node, key) {
    const value = node && node[key] && node[key].value ? node[key].value : "";
    if (!value) return false;
    const trimmed = String(value).trim();
    if (!trimmed || trimmed === "[]" || trimmed === "null" || trimmed === "{}") return false;
    return true;
  }

  /** Product card image widths (match product-media.liquid / card-gallery). */
  const CARD_IMAGE_WIDTHS = [220, 360, 420];
  const CARD_IMAGE_SIZE = 420;
  const CARD_IMAGE_SIZES = "auto, (max-width: 1024px) 220px, (min-width: 1024px) 360px, (min-width:1400px) 420px";

  /**
   * Return image URL with Shopify CDN width parameter (replace or append).
   * @param {string} url - Image URL (may already have width=)
   * @param {number} w - Width in pixels
   * @returns {string}
   */
  function imageUrlWithWidth(url, w) {
    if (!url) return "";
    const hasWidth = /[?&]width=\d+/.test(url);
    if (hasWidth) return url.replace(/([?&])width=\d+/, "$1width=" + w);
    return (url.indexOf("?") !== -1 ? url + "&" : url + "?") + "width=" + w;
  }

  /**
   * Build srcset string for product card image (220w, 360w, 420w).
   * @param {string} url - Base image URL
   * @param {number[]} [widths] - Optional widths, default CARD_IMAGE_WIDTHS
   * @returns {string}
   */
  function getSrcSet(url, widths) {
    if (!url) return "";
    widths = widths || CARD_IMAGE_WIDTHS;
    const parts = [];
    for (let i = 0; i < widths.length; i++) {
      parts.push(imageUrlWithWidth(url, widths[i]) + " " + widths[i] + "w");
    }
    return parts.join(", ");
  }

  /**
   * Parse JudgeMe badge HTML for data-average-rating and data-number-of-reviews.
   * Matches Liquid: product.metafields.judgeme.badge (single or double quotes in HTML).
   * @param {string} htmlString - Raw HTML from judgeme badge metafield value
   * @returns {{ averageRating: string|null, numberOfReviews: string|null }}
   */
  function parseJudgemeBadge(htmlString) {
    if (!htmlString || typeof htmlString !== "string") return { averageRating: null, numberOfReviews: null };
    const str = htmlString.trim();
    let averageRating = null;
    let numberOfReviews = null;
    const ratingMatch = str.match(/data-average-rating\s*=\s*['"]([^'"]*)['"]/);
    if (ratingMatch && ratingMatch[1] !== undefined) {
      const r = parseFloat(ratingMatch[1]);
      if (!isNaN(r)) averageRating = r.toFixed(1);
    }
    const countMatch =
      str.match(/data-number-of-reviews\s*=\s*['"]([^'"]*)['"]/) ||
      str.match(/data-review-count\s*=\s*['"]([^'"]*)['"]/);
    if (countMatch && countMatch[1] !== undefined) {
      const c = String(countMatch[1]).trim();
      if (c !== "") numberOfReviews = c;
    }
    return { averageRating: averageRating, numberOfReviews: numberOfReviews };
  }

  /**
   * Build HTML for a single product card.
   * @param {Object} data - Normalized product data (see buildProductCardsHTML for shape).
   * @returns {string} HTML string for one swiper-slide + product-card.
   */
  function buildProductCardHTML(data) {
    const productId = data.productId;
    const variantId = data.variantId;
    const productUrl = data.productUrl;
    const title = data.title;
    const titleEscaped = (title || "").replace(/"/g, "&quot;");
    const price = data.price;
    const currencySymbol = data.currencySymbol;
    const formattedCompareAtPrice = data.formattedCompareAtPrice;
    const isOnSale = data.isOnSale;
    const discountPercent = data.discountPercent;
    const imageUrl = data.imageUrl;
    const imageAlt = data.imageAlt;
    const imageWidth = data.imageWidth || 300;
    const imageHeight = data.imageHeight || 300;
    const ribbonName = data.ribbonName;
    const ribbonColor = data.ribbonColor || "#2563eb";
    const badges = data.badges || [];
    const isOutOfStock = data.isOutOfStock;
    const showViewBundle = data.showViewBundle;
    const isPreOrder = data.isPreOrder;
    const isAvailable = data.isAvailable;
    const hasMultipleVariants = data.hasMultipleVariants;
    const needsQuickBuy = data.needsQuickBuy === true || hasMultipleVariants;
    const averageRating = data.averageRating;
    const numberOfReviews = data.numberOfReviews;
    const editionTag = data.editionTag;
    const urgencyLabel = data.urgencyLabel || "";
    const urgencyEmoji = data.urgencyEmoji || "";
    const sectionId = data.sectionId;
    const quickAddColorScheme = data.quickAddColorScheme || "";
    const index = data.index != null ? data.index : 0;
    let mediaArray = data.mediaArray || [];
    const productHandle = data.productHandle != null ? data.productHandle : data.productUrl ? data.productUrl.replace(/^\/products\//, "").replace(/\/$/, "") : "";
    const productVariantsSize = data.productVariantsSize != null ? data.productVariantsSize : 0;
    const compareAtPriceRaw = data.compareAtPriceRaw != null ? data.compareAtPriceRaw : 0;
    const variantPriceRaw = data.variantPriceRaw != null ? data.variantPriceRaw : data.price;
    const variantAvailable = data.variantAvailable != null ? data.variantAvailable : data.isAvailable;
    const inventoryQuantity = data.inventoryQuantity != null ? data.inventoryQuantity : 0;
    const tagsJoined = data.tagsJoined != null ? data.tagsJoined : "";
    const preorderValue = data.preorderValue != null ? data.preorderValue : data.isPreOrder ? "yes" : "no";
    const l1CategoryName = data.l1CategoryName || "";
    const l2CategoryName = data.l2CategoryName || "";
    const l3CategoryName = data.l3CategoryName || "";
    const productCategoryId = data.productCategoryId || "";
    const productSubCategoryId = data.productSubCategoryId || "";
    const productCategorySlug = data.productCategorySlug || "";
    const noSwatchSelected = data.noSwatchSelected === true;
    const productTransition = data.productTransition != null ? data.productTransition : "";
    const digitalBookTagText = data.digitalBookTagText || "";
    const isDigitalBook = data.isDigitalBook === true;
    const hasShikshaMahotsav = data.hasShikshaMahotsav === true;
    const hasFlashSale = data.hasFlashSale === true;
    const ageTag = data.ageTag || "";
    if (mediaArray.length === 0 && imageUrl) mediaArray = [{ url: imageUrl, alt: imageAlt, width: imageWidth, height: imageHeight }];

    const ageTagHTML = ageTag
      ? `<div class="pw-product-age-tag pw-product-age-tag--card"><span class="pw-product-age-tag__text">${String(ageTag).replace(/</g, "&lt;").replace(/>/g, "&gt;")}</span></div>`
      : "";

    const badgesHTML = badges
      .map(function (b) {
        return `<span class="badge ${b.classes}">${b.text}</span>`;
      })
      .join("");

    const slideCount = mediaArray.length;
    const timelineScope = Array.from({ length: slideCount }, function (_, t) {
      return "--slide-" + (t + 1);
    }).join(", ");
    const arrowSvg = '<span class="svg-wrapper icon-arrow"><svg width="28" height="40" viewBox="0 0 28 40" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M28 0H8C3.58172 0 0 3.58172 0 8V32C0 36.4183 3.58172 40 8 40H28V0Z" fill="black" fill-opacity="0.62"></path><path fill-rule="evenodd" clip-rule="evenodd" d="M11.2094 15.2302C10.9223 15.5287 10.9316 16.0035 11.2302 16.2906L15.1679 20L11.2302 23.7094C10.9316 23.9965 10.9223 24.4713 11.2094 24.7698C11.4965 25.0684 11.9713 25.0777 12.2698 24.7906L16.7698 20.5406C16.9169 20.3992 17 20.204 17 20C17 19.796 16.9169 19.6008 16.7698 19.4594L12.2698 15.2094C11.9713 14.9223 11.4965 14.9316 11.2094 15.2302Z" fill="white"></path></svg></span>';
    const arrowSvgPrev = arrowSvg;
    const arrowSvgNext = arrowSvg;

    const isFirstCard = index === 0;
    const loadingFirst = isFirstCard ? "eager" : "lazy";
    const fetchPriorityFirst = isFirstCard ? "auto" : "auto";
    const loadingRest = "lazy";
    const fetchPriorityRest = "auto";
    const transitionAttr = ` transitiontoproduct="false"`;
    const focalStyle = function (med) {
      const fp = med && med.focalPoint ? String(med.focalPoint).replace(/"/g, "&quot;") : "50% 50%";
      return `--focal-point: ${fp}`;
    };

    const variantUrl = productUrl + (variantId ? "?variant=" + variantId : "");
    const cardGalleryId = `card-gallery-ANlZ3VjRKRnVFMjhpW__product_card_gallery_atyhU3${index > 0 ? "-" + index : ""}`;
    const totalCount = data.totalCount != null ? data.totalCount : index + 1;

    // Lightweight poster shown immediately (first image only, no slideshow-component). The full
    // gallery below is kept inert inside a <template> so its extra images never download and the
    // slideshow-component never upgrades/initializes until the card scrolls into view (see
    // revealCardGallery / initLazyCardGalleries).
    let posterHTML = "";
    if (mediaArray.length > 0) {
      const firstMed = mediaArray[0];
      const firstSrcUrl = imageUrlWithWidth(firstMed.url, CARD_IMAGE_SIZE);
      const firstSrcSet = getSrcSet(firstMed.url);
      const firstDataMaxRes = firstMed.url ? ` data_max_resolution="${String(firstSrcUrl).replace(/"/g, "&quot;")}"` : "";
      const firstStyle = focalStyle(firstMed);
      const firstRatio = firstMed.width && firstMed.height ? firstMed.height / firstMed.width : 1;
      const firstSlideId = firstMed.id || `${productId}-0`;
      posterHTML = `<a class="contents card-gallery-poster${isOutOfStock ? " card-gallery__media-wrapper" : ""}" ref="cardGalleryLink" href="${variantUrl}" aria-label="${titleEscaped}"><div class="product-media-container media-fit product-media-container--image" style="--product-media-fit: cover"><div class="product-media" style="--ratio: ${firstRatio}" data-media-id="${firstSlideId}"><img src="${firstSrcUrl}" alt="${(firstMed.alt || titleEscaped).replace(/"/g, "&quot;")}" srcset="${firstSrcSet}" width="${CARD_IMAGE_SIZE}" height="${Math.round(CARD_IMAGE_SIZE * firstRatio)}" loading="${loadingFirst}" sizes="${CARD_IMAGE_SIZES}" class="product-media__image"${transitionAttr}${firstDataMaxRes} fetchpriority="${fetchPriorityFirst}" style="${firstStyle}"></div></div></a>`;
    }

    let galleryInnerHTML;
    if (slideCount > 1) {
      let slidesHTML = "";
      for (let s = 0; s < mediaArray.length; s++) {
        const med = mediaArray[s];
        const slideId = med.id || `${productId}-${s}`;
        const ariaHidden = s === 0 ? "false" : "true";
        const imgLoading = s === 0 ? loadingFirst : loadingRest;
        const imgFetchPriority = s === 0 ? fetchPriorityFirst : "auto";
        const srcUrl = imageUrlWithWidth(med.url, CARD_IMAGE_SIZE);
        const srcSetStr = getSrcSet(med.url);
        const dataMaxRes = med.url ? ` data_max_resolution="${String(imageUrlWithWidth(med.url, CARD_IMAGE_SIZE)).replace(/"/g, "&quot;")}"` : "";
        const imgStyle = focalStyle(med);
        const variantImageAttr = s === 0 ? ' variant-image=""' : "";
        const ratio = med.width && med.height ? med.height / med.width : 1;
        slidesHTML += `<slideshow-slide ref="slides[]" aria-hidden="${ariaHidden}" style="view-timeline-name: --slide-${s + 1}; --product-media-fit: cover" class="product-media-container media-fit product-media-container--image"${variantImageAttr} slide-id="${slideId}"><div class="product-media" style="--ratio: ${ratio}" data-media-id="${slideId}"><img src="${srcUrl}" alt="${(med.alt || titleEscaped).replace(/"/g, "&quot;")}" srcset="${srcSetStr}" width="${CARD_IMAGE_SIZE}" height="${Math.round(CARD_IMAGE_SIZE * ratio)}" loading="${imgLoading}" sizes="${CARD_IMAGE_SIZES}" class="product-media__image"${transitionAttr}${dataMaxRes} fetchpriority="${imgFetchPriority}" style="${imgStyle}"></div></slideshow-slide>`;
      }
      let dotsHTML = "";
      for (let d = 0; d < slideCount; d++) {
        dotsHTML += `<li><button class="slideshow-control button button-unstyled" style="animation-timeline: --slide-${d + 1}" aria-label="Slide ${d + 1} of ${slideCount}" on:click="/select/${d}" ref="dots[]" ${d === 0 ? 'aria-selected="true"' : 'aria-selected="false"'}>${d + 1}</button></li>`;
      }
      const fullGalleryHTML = `
        <a class="contents${isOutOfStock ? " card-gallery__media-wrapper" : ""}" ref="cardGalleryLink" href="${variantUrl}" aria-label="${titleEscaped}">
          <slideshow-component ref="slideshow" style="--slideshow-timeline: ${timelineScope}" initial-slide="0" infinite="" data-generic-media-size="${slideCount - 1}"${slideCount > 2 ? ' disabled="false" paused="" actioned=""' : ""}>
            <slideshow-container ref="slideshowContainer">
              <slideshow-arrows position="center">
                <button class="slideshow-control slideshow-control--previous slideshow-control--style-arrow button button-unstyled button-unstyled--transparent flip-x" aria-label="Previous slide" on:click="/previous" ref="previous">${arrowSvgPrev}</button>
                <button class="slideshow-control slideshow-control--next slideshow-control--style-arrow button button-unstyled button-unstyled--transparent" aria-label="Next slide" on:click="/next" ref="next">${arrowSvgNext}</button>
              </slideshow-arrows>
              <slideshow-slides tabindex="-1" ref="scroller">${slidesHTML}</slideshow-slides>
            </slideshow-container>
            <slideshow-controls ref="slideshowControls" pagination-position="left" scroll-mode="vertical" controls-on-media="">
              <ol class="slideshow-controls__dots">${dotsHTML}</ol>
            </slideshow-controls>
          </slideshow-component>
        </a>`;
      galleryInnerHTML = `${posterHTML}<template class="card-gallery-template">${fullGalleryHTML}</template>`;
    } else if (mediaArray.length > 0) {
      const single = mediaArray[0];
      const singleSrcUrl = imageUrlWithWidth(single.url, CARD_IMAGE_SIZE);
      const singleSrcSet = getSrcSet(single.url);
      const singleDataMaxRes = single.url ? ` data_max_resolution="${String(singleSrcUrl).replace(/"/g, "&quot;")}"` : "";
      const singleStyle = focalStyle(single);
      const singleRatio = single.width && single.height ? single.height / single.width : 0.6666666666666666;
      const singleSlideId = single.id || `${productId}-0`;
      const fullGalleryHTML = `<a class="contents" ref="cardGalleryLink" href="${variantUrl}" aria-label="${titleEscaped}"><slideshow-component ref="slideshow" style="--slideshow-timeline: --slide-1" initial-slide="0" infinite="" data-generic-media-size="0"><slideshow-container ref="slideshowContainer"><slideshow-slides tabindex="-1" ref="scroller"><slideshow-slide ref="slides[]" aria-hidden="false" style="view-timeline-name: --slide-1; --product-media-fit: cover" class="product-media-container media-fit product-media-container--image" variant-image="" slide-id="${singleSlideId}"><div class="product-media" style="--ratio: ${singleRatio}" data-media-id="${singleSlideId}"><img src="${singleSrcUrl}" alt="${(single.alt || titleEscaped).replace(/"/g, "&quot;")}" srcset="${singleSrcSet}" width="${CARD_IMAGE_SIZE}" height="${Math.round(CARD_IMAGE_SIZE * singleRatio)}" loading="${loadingFirst}" sizes="${CARD_IMAGE_SIZES}" class="product-media__image"${transitionAttr}${singleDataMaxRes} fetchpriority="${fetchPriorityFirst}" style="${singleStyle}"></div></slideshow-slide></slideshow-slides></slideshow-container></slideshow-component></a>`;
      galleryInnerHTML = `${posterHTML}<template class="card-gallery-template">${fullGalleryHTML}</template>`;
    } else {
      let singleImg;
      if (mediaArray.length > 0) {
        const single = mediaArray[0];
        const singleSrcUrl = imageUrlWithWidth(single.url, CARD_IMAGE_SIZE);
        const singleSrcSet = getSrcSet(single.url);
        const singleDataMaxRes = single.url ? ` data_max_resolution="${String(singleSrcUrl).replace(/"/g, "&quot;")}"` : "";
        const singleStyle = focalStyle(single);
        const singleRatio = single.width && single.height ? single.height / single.width : 0.6666666666666666;
        const singleSlideId = single.id || `${productId}-0`;
        singleImg = `<slideshow-component ref="slideshow" style="--slideshow-timeline: --slide-1" initial-slide="0" infinite="" data-generic-media-size="0"><slideshow-container ref="slideshowContainer"><slideshow-slides tabindex="-1" ref="scroller"><slideshow-slide ref="slides[]" aria-hidden="false" style="view-timeline-name: --slide-1; --product-media-fit: cover" class="product-media-container media-fit product-media-container--image" variant-image="" slide-id="${singleSlideId}"><div class="product-media" style="--ratio: ${singleRatio}" data-media-id="${singleSlideId}"><img src="${singleSrcUrl}" alt="${(single.alt || titleEscaped).replace(/"/g, "&quot;")}" srcset="${singleSrcSet}" width="${CARD_IMAGE_SIZE}" height="${Math.round(CARD_IMAGE_SIZE * singleRatio)}" loading="${loadingFirst}" sizes="${CARD_IMAGE_SIZES}" class="product-media__image"${transitionAttr}${singleDataMaxRes} fetchpriority="${fetchPriorityFirst}" style="${singleStyle}"></div></slideshow-slide></slideshow-slides></slideshow-container></slideshow-component>`;
      } else {
        singleImg = '<div class="placeholder-image" style="background: #f0f0f0; width: 100%; height: 204px; display: flex; align-items: center; justify-content: center;"><span>No Image</span></div>';
      }
      galleryInnerHTML = `<a class="contents" ref="cardGalleryLink" href="${variantUrl}" aria-label="${titleEscaped}">${singleImg}</a>`;
    }

    const ribbonHTML = ribbonName
      ? `
        <div class="card-badges custom-badge-tag">
          <div class="badge-wrapper meta-ribbon">
            <svg width="8" height="15" viewBox="0 0 8 15" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
              <path d="M8 -1V15C5.52572 15 3.37611 13.2988 2.80754 10.8908L0 -1H8Z" fill="${ribbonColor}"></path>
            </svg>
            <span class="badge badge-tags meta-ribbon" style="background-color: ${ribbonColor}; color: #ffffff"> ${ribbonName} </span>
            <svg width="8" height="15" viewBox="0 0 8 15" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
              <path d="M0 -1V15C2.47428 15 4.62389 13.2988 5.19246 10.8908L8 -1H0Z" fill="${ribbonColor}"></path>
            </svg>
          </div>
        </div>`
      : "";

    const outOfStockHTML = isOutOfStock ? '<div class="card-gallery__out-of-stock-banner"><span class="card-gallery__out-of-stock-text">Out of stock</span></div>' : "";

    const digitalBookHTML = digitalBookTagText
      ? `<div class="pw-digital-book-tag product-card">${String(digitalBookTagText).replace(/</g, "&lt;").replace(/>/g, "&gt;")}</div>`
      : "";

    const saleAmt = Number(variantPriceRaw);
    const compareAmt = Number(compareAtPriceRaw);
    let listAmt = compareAmt;
    if (!compareAmt || compareAmt <= 0 || compareAmt <= saleAmt) listAmt = saleAmt;
    const discountAmt = listAmt > saleAmt ? listAmt - saleAmt : 0;
    const productGtmData = {
      product_id: productId,
      product_title: title,
      product_img_url: mediaArray.length > 0 ? imageUrlWithWidth(mediaArray[0].url, 500) : null,
      product_slug: productHandle,
      product_moq: 50,
      product_discount_percent: discountPercent || 0,
      is_in_stock: variantAvailable,
      product_price: listAmt,
      product_discount_price: discountAmt > 0 ? discountAmt : null,
      product_discounted_price: saleAmt,
      product_tag: tagsJoined || null,
      preorder: preorderValue,
      product_available_quantity: inventoryQuantity,
      product_category_id: productCategoryId || null,
      product_sub_category_id: productSubCategoryId || null,
      product_category_slug: productCategorySlug || null,
      l1_category_name: l1CategoryName || null,
      l2_category_name: l2CategoryName || null,
      l3_category_name: l3CategoryName || null,
      product_variant_id: variantId,
    };
    const productGtmJson = JSON.stringify(productGtmData).replace(/'/g, "\\u0027");

    const escapeAttr = (value) => String(value == null ? "" : value).replace(/"/g, "&quot;");
    const cartLinePropsHTML = [
      `<input type="hidden" name="properties[_product_available_quantity]" value="${escapeAttr(inventoryQuantity)}" />`,
      `<input type="hidden" name="properties[_product_price]" value="${escapeAttr(compareAtPriceRaw || "")}" />`,
      `<input type="hidden" name="properties[_product_tag]" value="${escapeAttr(tagsJoined || ribbonName)}" />`,
      `<input type="hidden" name="properties[_preorder]" value="${escapeAttr(preorderValue)}" />`,
      `<input type="hidden" name="properties[_l1_category_name]" value="${escapeAttr(l1CategoryName)}" />`,
      `<input type="hidden" name="properties[_l2_category_name]" value="${escapeAttr(l2CategoryName)}" />`,
      `<input type="hidden" name="properties[_l3_category_name]" value="${escapeAttr(l3CategoryName)}" />`,
    ].join("");
    const basePropsHTML = `<input type="hidden" name="properties[_digital_book]" value="${isDigitalBook ? "true" : "false"}" /><input type="hidden" name="properties[_purchase_type]" value="null" />${cartLinePropsHTML}`;

    let ctaHTML;
    const quickAddFormId = `QuickAdd-ProductForm-${productId}-${cardGalleryId}`;
    const buttonText = "Add";
    const variantMediaUrl = mediaArray.length > 0 ? imageUrlWithWidth(mediaArray[0].url, 100) : "";
    const isFlyBundleAddable = data.isFlyBundleAddable;
    const flyVariantIds = data.flyVariantIds || "";
    const flyOfferId = data.flyOfferId || "";
    const flyChildDigitalMap = data.flyChildDigitalMap || "";

    if (needsQuickBuy && !showViewBundle && !isOutOfStock) {
      ctaHTML = `
        <quick-add-component class="quick-add color-scheme-1" ref="quickAdd" data-product-title="${titleEscaped}">
          <product-form-component data-section-id="${sectionId}" data-product-id="${productId}" data-product-gtm='${productGtmJson}' class="quick-add__product-form-component quick-add__product-form-component--multi-variant">
            <div class="visually-hidden" aria-live="assertive" role="status" aria-atomic="true" ref="liveRegion"></div>
            <form method="post" action="/cart/add" id="${quickAddFormId}" accept-charset="UTF-8" class="shopify-product-form" enctype="multipart/form-data" novalidate="novalidate" data-type="add-to-cart-form">
              <input type="hidden" name="form_type" value="product" />
              <input type="hidden" name="utf8" value="✓" />
              <input type="hidden" name="id" ref="variantId" value="${variantId}" disabled/>
              <input type="hidden" name="quantity" value="1" />
              ${basePropsHTML}
              <div data-dr="back-in-stock-inline-button" data-product-id="" data-selected-variant-id="" data-custom-text="🔔 Notify Me"></div>
              <button type="button" class="button quick-add__button quick-add__button--choose" data-quick-buy-open on:click="quick-add-component/handleClick">
                <span class="quick-add__button-loader"><svg class="quick-add__button-spinner" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-dasharray="43.98" stroke-dashoffset="10.99"/></svg></span>
                <span class="add-to-cart-text"><span class="add-to-cart-text__content">Add</span></span>
              </button>
              <input type="hidden" name="product-id" value="${productId}" /><input type="hidden" name="section-id" value="${sectionId}" />
            </form>
          </product-form-component>
        </quick-add-component>`;
    } else if (isFlyBundleAddable) {
      const flyDigitalAttr = flyChildDigitalMap ? ` data-fly-digital-map="${String(flyChildDigitalMap).replace(/"/g, "&quot;")}"` : "";
      ctaHTML = `
        <div class="quick-add color-${quickAddColorScheme}">
          <button
            type="button"
            class="button quick-add__button quick-add__button--add fly-bundle-add-btn"
            data-fly-variant-ids="${String(flyVariantIds).replace(/"/g, "&quot;")}"
            data-fly-offer-id="${String(flyOfferId).replace(/"/g, "&quot;")}"
            data-fly-bundle-name="${titleEscaped}"${flyDigitalAttr}
          >
            <span class="add-to-cart-text">
              <span class="add-to-cart-text__content">Add</span>
            </span>
            <span aria-hidden="true" class="add-to-cart-text--added">
              <span>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 8.5L7 12.5L13 3.5" stroke="#F1EFFF" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </span>
            </span>
          </button>
        </div>`;
    } else if (showViewBundle) {
      ctaHTML = `
        <quick-add-component class="quick-add color-scheme-1 " ref="quickAdd" data-product-title="${titleEscaped}">
          <product-form-component data-section-id="${sectionId}" data-product-id="${productId}" data-product-gtm='${productGtmJson}' on:submit="/handleSubmit" class="quick-add__product-form-component quick-add__product-form-component--single-option quick-add__product-form-component--single-variant">
            <div class="visually-hidden" aria-live="assertive" role="status" aria-atomic="true" ref="liveRegion"></div>
            <form method="post" action="/cart/add" id="${quickAddFormId}" accept-charset="UTF-8" class="shopify-product-form" enctype="multipart/form-data" novalidate="novalidate" data-type="add-to-cart-form">
              <input type="hidden" name="form_type" value="product" /><input type="hidden" name="utf8" value="✓" /><input type="hidden" name="id" ref="variantId" value="${variantId}">
              <input type="hidden" name="quantity" value="1" />
              ${basePropsHTML}
              <div data-dr="back-in-stock-inline-button" data-product-id="" data-selected-variant-id="" data-custom-text="🔔 Notify Me"></div>
              <a href="${productUrl}" class="button button quick-add__button quick-add__button--add view-bundle-button" id="">
                <span class="add-to-cart-text">
                  <span class="add-to-cart-text__content">View Bundle</span>
                </span>
              </a>
              <input type="hidden" name="product-id" value="${productId}" /><input type="hidden" name="section-id" value="${sectionId}" /></form>
          </product-form-component>
        </quick-add-component>`;
    } else if (isOutOfStock) {
      ctaHTML = `
        <quick-add-component class="quick-add color-scheme-1" ref="quickAdd" data-product-title="${titleEscaped}">
          <product-form-component data-section-id="${sectionId}" data-product-id="${productId}" data-product-gtm='${productGtmJson}' on:submit="/handleSubmit" class="quick-add__product-form-component quick-add__product-form-component--single-option quick-add__product-form-component--single-variant">
            <div class="visually-hidden" aria-live="assertive" role="status" aria-atomic="true" ref="liveRegion"></div>
            <form method="post" action="/cart/add" id="${quickAddFormId}" accept-charset="UTF-8" class="shopify-product-form" enctype="multipart/form-data" novalidate="novalidate" data-type="add-to-cart-form">
              <input type="hidden" name="form_type" value="product" /><input type="hidden" name="utf8" value="✓" /><input type="hidden" name="id" ref="variantId" value="${variantId}" disabled="" />
              <input type="hidden" name="quantity" value="1" />
              ${basePropsHTML}
              <div data-dr="back-in-stock-inline-button" data-product-id="" data-selected-variant-id="" data-custom-text="🔔 Notify Me"></div>
              <add-to-cart-component ref="addToCartButtonContainer" data-product-variant-media="${variantMediaUrl}" data-add-to-cart-animation="false" data-is-deal-day="" data-is-combo-pack="">
                <span class="add-to-cart-loader hidden" ref="addToCartLoader" aria-hidden="true"><span class="loader-spinner"></span></span>
                <button id="" type="button" name="add" ref="addToCartButton" on:click="/handleClick" class="button button quick-add__button quick-add__button--add" disabled="">
                  <span class="add-to-cart-text"><span class="add-to-cart-text__content"> ${buttonText} </span></span>
                  <span aria-hidden="true" class="add-to-cart-text--added"><span><svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 8.5L7 12.5L13 3.5" stroke="#F1EFFF" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path></svg></span></span>
                </button>
              </add-to-cart-component>
              <input type="hidden" name="product-id" value="${productId}" /><input type="hidden" name="section-id" value="${sectionId}" /></form>
          </product-form-component>
        </quick-add-component>`;
    } else {
      ctaHTML = `
        <quick-add-component class="quick-add color-scheme-1" ref="quickAdd" data-product-title="${titleEscaped}">
          <product-form-component data-section-id="${sectionId}" data-product-id="${productId}" data-product-gtm='${productGtmJson}' on:submit="/handleSubmit" class="quick-add__product-form-component quick-add__product-form-component--single-option quick-add__product-form-component--single-variant">
            <div class="visually-hidden" aria-live="assertive" role="status" aria-atomic="true" ref="liveRegion"></div>
            <form method="post" action="/cart/add" id="${quickAddFormId}" accept-charset="UTF-8" class="shopify-product-form" enctype="multipart/form-data" novalidate="novalidate" data-type="add-to-cart-form">
              <input type="hidden" name="form_type" value="product" /><input type="hidden" name="utf8" value="✓" /><input type="hidden" name="id" ref="variantId" value="${variantId}" />
              <input type="hidden" name="quantity" value="1" />
              ${basePropsHTML}
              <div data-dr="back-in-stock-inline-button" data-product-id="" data-selected-variant-id="" data-custom-text="🔔 Notify Me"></div>
              <add-to-cart-component ref="addToCartButtonContainer" data-product-variant-media="${variantMediaUrl}" data-add-to-cart-animation="false" data-is-deal-day="" data-is-combo-pack="">
                <span class="add-to-cart-loader hidden" ref="addToCartLoader" aria-hidden="true"><span class="loader-spinner"></span></span>
                <button id="" type="button" name="add" ref="addToCartButton" on:click="/handleClick" class="button button quick-add__button quick-add__button--add" ${!isAvailable && !isPreOrder ? "disabled" : ""}>
                  <span class="add-to-cart-text"><span class="add-to-cart-text__content"> ${buttonText} </span></span>
                  <span aria-hidden="true" class="add-to-cart-text--added"><span><svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 8.5L7 12.5L13 3.5" stroke="#F1EFFF" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path></svg></span></span>
                </button>
              </add-to-cart-component>
              <input type="hidden" name="product-id" value="${productId}" /><input type="hidden" name="section-id" value="${sectionId}" /></form>
          </product-form-component>
        </quick-add-component>`;
    }

    const editionHTML = editionTag
      ? `
        <div class="text-block-tag-container text-block-tag-${cardGalleryId}" data-text-block-tag="" data-product-id="${productId || ""}" data-variant-id="${variantId || ""}">
          <div class="text-block-tag">
            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="24" viewBox="0 0 15 24" fill="none">
              <path d="M6.6084 4.80005L2.47458 15.2597L3.88701 19.5453L7.84769 17.3832L11.9815 6.92358L6.6084 4.80005ZM7.07958 16.7069L4.43913 18.1483L3.49751 15.2912L6.08823 8.73604L9.67029 10.1517L7.07958 16.7069ZM6.44215 7.84052L7.14999 6.04949L10.7321 7.46518L10.0242 9.25621L6.44215 7.84052Z" fill="#FB6514"></path>
              <path d="M7.07958 16.7069L4.43913 18.1483L3.49751 15.2912L6.08823 8.73604L9.67029 10.1517L7.07958 16.7069Z" fill="#FB6514"></path>
              <path d="M6.44215 7.84052L7.14999 6.04949L10.7321 7.46518L10.0242 9.25621L6.44215 7.84052Z" fill="#FB6514"></path>
            </svg>
            <span class="text-block-tag-text">${(editionTag || "").replace(/</g, "&lt;").replace(/>/g, "&gt;")}</span>
          </div>
        </div>`
      : "";

    const urgencyHTML = urgencyLabel
      ? `<div class="pw-urgency-label"><span class="pw-urgency-label__emoji" aria-hidden="true">${urgencyEmoji}</span><span class="pw-urgency-label__text">${String(urgencyLabel).replace(/</g, "&lt;").replace(/>/g, "&gt;")}</span></div>`
      : "";

    const flashSaleBoltSVG =
      '<svg class="flash-sale-bolt" width="14" height="18" viewBox="0 0 14 18" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false"><path d="M5.82853 10.2122C5.63149 9.93211 5.3708 9.89838 5.1454 9.82186C4.17508 9.4934 3.33107 8.94378 2.50925 8.34997C2.40561 8.28229 2.32713 8.18022 2.2841 8.05712C2.24108 7.93402 2.23551 7.79565 2.26813 7.66015C2.32267 7.2782 2.40147 6.8978 2.50397 6.52165C2.72355 5.80749 2.97682 5.09848 3.23034 4.38853C3.44439 3.78265 3.6501 3.17426 3.89933 2.58169C4.11247 2.06622 4.38958 1.57422 4.63348 1.0683C4.90848 0.501092 5.33064 0.320073 5.86294 0.390501C6.33448 0.452807 6.82 0.464495 7.27961 0.565981C8.1985 0.780705 9.10511 1.03947 10.0152 1.28115C10.2983 1.34083 10.539 1.51337 10.6911 1.76565C10.4019 2.29385 10.1239 2.83787 9.81271 3.36106C9.51552 3.86121 9.09472 4.2837 8.8613 4.88113C9.28739 5.076 9.73502 5.22353 10.1978 5.32161C10.735 5.43231 11.272 5.54003 11.8082 5.65448C12.021 5.69645 12.2188 5.78516 12.3898 5.9153C12.5608 6.04543 12.7014 6.21431 12.8033 6.41181C12.5394 7.49102 12.0332 8.51227 11.3306 9.38265C10.126 10.8517 8.80814 12.2124 7.39388 13.4472C6.3749 14.3357 5.32792 15.1866 4.29482 16.0567C3.90566 16.3845 3.46267 16.5627 3.01388 16.7182C2.9637 16.7382 2.91086 16.7436 2.86115 16.7337C2.81144 16.7238 2.76677 16.6991 2.73205 16.6623C2.69732 16.6255 2.67388 16.5779 2.66429 16.5249C2.6547 16.4718 2.65933 16.4153 2.67768 16.3616C2.86312 15.8072 3.08466 15.2635 3.34055 14.7348C3.8386 13.7632 4.3836 12.8149 4.91033 11.8574C5.2122 11.3148 5.51237 10.7788 5.82853 10.2122ZM3.2318 7.67726C3.76108 8.14229 4.36198 8.51134 5.01437 8.77206C5.53413 8.99183 6.06183 9.20516 6.57756 9.43699C6.91494 9.59103 6.97127 9.88386 6.7531 10.2173C5.86163 11.5819 5.13617 13.0338 4.39943 14.4808C4.36723 14.5428 4.37671 14.6243 4.36019 14.7473C4.46848 14.7004 4.5735 14.643 4.67385 14.5761C5.53579 13.847 6.4309 13.1703 7.25708 12.3885C8.3253 11.382 9.33625 10.3064 10.283 9.16908C10.6354 8.7316 10.9594 8.27 11.2523 7.78815C11.4843 7.42129 11.6484 7.01561 11.7346 6.5961C11.4722 6.50988 11.2421 6.41675 11.0084 6.36531C10.6216 6.27413 10.2161 6.23765 9.83769 6.12437C9.22272 5.93944 8.61639 5.72457 8.01861 5.49748C7.74024 5.38964 7.65928 5.15041 7.79043 4.84448C7.91773 4.56525 8.06667 4.29587 8.23554 4.03948C8.48663 3.63431 8.75611 3.24135 9.0072 2.83618C9.15954 2.59417 9.2565 2.33715 9.16128 2.07661C8.08481 1.58899 6.44655 1.28058 5.43792 1.37145C4.61891 2.5425 3.29957 6.28561 3.2318 7.67726Z" fill="#1B2124"/><path d="M3.2318 7.67726C3.76108 8.14229 4.36198 8.51134 5.01437 8.77206C5.53413 8.99183 6.06183 9.20516 6.57756 9.43699C6.91494 9.59103 6.97127 9.88386 6.7531 10.2173C5.86163 11.5819 5.13617 13.0338 4.39943 14.4808C4.36723 14.5428 4.37671 14.6243 4.36019 14.7473C4.46848 14.7004 4.5735 14.643 4.67385 14.5761C5.53579 13.847 6.4309 13.1703 7.25708 12.3885C8.3253 11.382 9.33625 10.3064 10.283 9.16908C10.6354 8.7316 10.9594 8.27 11.2523 7.78815C11.4843 7.42129 11.6484 7.01561 11.7346 6.5961C11.4722 6.50988 11.2421 6.41675 11.0084 6.36531C10.6216 6.27413 10.2161 6.23765 9.83769 6.12437C9.22272 5.93944 8.61639 5.72457 8.01861 5.49748C7.74024 5.38964 7.65928 5.15041 7.79043 4.84448C7.91773 4.56525 8.06667 4.29587 8.23554 4.03948C8.48663 3.63431 8.75611 3.24135 9.0072 2.83618C9.15954 2.59417 9.2565 2.33715 9.16128 2.07661C8.08481 1.58899 6.44655 1.28058 5.43792 1.37145C4.61891 2.5425 3.29957 6.28561 3.2318 7.67726Z" fill="#1B2124"/><path d="M4.08769 8.17382C3.63301 7.85228 3.41938 7.30573 3.55426 6.74975C3.95058 5.11602 5.02957 2.4502 5.65496 1.55574C6.59253 1.4719 8.1067 1.75706 9.10648 2.21234C9.19566 2.45048 8.91055 2.99162 8.77078 3.21998C8.53798 3.59575 8.28813 3.96018 8.05532 4.33595C7.89876 4.57373 7.76066 4.82355 7.64263 5.08252C7.52262 5.36653 7.59531 5.58797 7.85419 5.68812C8.40754 5.90222 8.96972 6.1015 9.54079 6.26952C9.89167 6.37457 10.2676 6.40841 10.6263 6.49297C11.026 6.58743 11.2503 7.00641 11.0801 7.38746C11.0147 7.53394 10.9386 7.67613 10.8524 7.81254C10.581 8.25574 10.2813 8.68028 9.95556 9.08263C9.07792 10.1365 8.14083 11.1331 7.15071 12.0658C6.3838 12.7944 5.83422 13.4649 5.03745 14.1415C4.94441 14.2036 4.84703 14.2568 4.74663 14.3003C4.76195 14.1862 4.75316 14.1107 4.78302 14.0531C5.46292 12.7106 6.43245 11.3079 7.25423 10.0415C7.4522 9.73605 7.40315 9.46505 7.08717 9.32161C6.61139 9.11344 5.55133 8.92624 5.06705 8.72199C4.72672 8.58569 4.39773 8.39309 4.08769 8.17382Z" fill="url(#flash-sale-bolt-grad-js)"/><defs><linearGradient id="flash-sale-bolt-grad-js" x1="8.59318" y1="2.24491" x2="6.34176" y2="14.5952" gradientUnits="userSpaceOnUse"><stop stop-color="#FEDA8D"/><stop offset="1" stop-color="#CEA653"/></linearGradient></defs></svg>';
    const salePriceInner = hasShikshaMahotsav
      ? `<span class="shiksha-price-badge__text"><span class="shiksha-price-badge__currency">${currencySymbol}</span><span class="shiksha-price-badge__amount">${price}</span></span>`
      : `${currencySymbol}${price}`;
    const salePriceGroup = isOnSale
      ? `<span role="group"><span class="visually-hidden">Sale price&nbsp;</span><span class="main-price price">${salePriceInner}</span></span>`
      : `<span class="no-compare-price-row"><span class="mrp-label">M.R.P</span><span class="no-compare-price price">${salePriceInner}</span></span>`;
    const comparePriceGroup = isOnSale ? `<span role="group"><span class="visually-hidden">Regular price&nbsp;</span><span class="mrp-label">M.R.P</span><span class="compare-at-price">${currencySymbol} ${formattedCompareAtPrice}</span></span>` : "";
    let discountContainerHTML = "";
    if (isOnSale && !hasShikshaMahotsav) {
      if (hasFlashSale) {
        discountContainerHTML = `<span class="discount-container discount-container--flash-sale" role="group" style=""><span class="flash-sale-bolt card_product_price_percentoff card_product_price_percentoff--flash-sale"><svg width="14" height="18" viewBox="0 0 14 18" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M5.82956 10.2122C5.63253 9.93211 5.37183 9.89839 5.14644 9.82186C4.17612 9.4934 3.3321 8.94379 2.51028 8.34997C2.40665 8.2823 2.32817 8.18022 2.28514 8.05712C2.24212 7.93403 2.23655 7.79566 2.26916 7.66015C2.32371 7.2782 2.40251 6.8978 2.50501 6.52165C2.72459 5.80749 2.97786 5.09848 3.23137 4.38854C3.44542 3.78265 3.65114 3.17427 3.90037 2.58169C4.1135 2.06622 4.39062 1.57423 4.63452 1.0683C4.90952 0.501096 5.33168 0.320077 5.86398 0.390504C6.33552 0.452811 6.82103 0.464499 7.28065 0.565985C8.19954 0.780709 9.10615 1.03947 10.0163 1.28115C10.2994 1.34084 10.54 1.51338 10.6921 1.76566C10.403 2.29385 10.125 2.83787 9.81375 3.36106C9.51656 3.86121 9.09576 4.2837 8.86234 4.88113C9.28843 5.076 9.73606 5.22353 10.1988 5.32162C10.736 5.43231 11.273 5.54003 11.8092 5.65449C12.022 5.69646 12.2198 5.78517 12.3908 5.9153C12.5618 6.04543 12.7025 6.21431 12.8043 6.41181C12.5405 7.49102 12.0342 8.51228 11.3316 9.38265C10.1271 10.8517 8.80918 12.2124 7.39492 13.4472C6.37594 14.3357 5.32896 15.1866 4.29585 16.0567C3.9067 16.3845 3.46371 16.5627 3.01492 16.7182C2.96474 16.7382 2.9119 16.7436 2.86219 16.7337C2.81248 16.7238 2.76781 16.6991 2.73309 16.6623C2.69836 16.6255 2.67492 16.5779 2.66533 16.5249C2.65574 16.4718 2.66037 16.4153 2.67871 16.3616C2.86416 15.8072 3.0857 15.2635 3.34159 14.7348C3.83963 13.7632 4.38463 12.8149 4.91136 11.8574C5.21324 11.3148 5.51341 10.7788 5.82956 10.2122ZM3.23283 7.67726C3.76212 8.14229 4.36301 8.51134 5.01541 8.77207C5.53517 8.99183 6.06287 9.20516 6.5786 9.437C6.91597 9.59103 6.97231 9.88387 6.75414 10.2173C5.86267 11.5819 5.13721 13.0338 4.40047 14.4808C4.36827 14.5428 4.37775 14.6243 4.36122 14.7473C4.46952 14.7004 4.57454 14.643 4.67489 14.5761C5.53682 13.847 6.43193 13.1703 7.25812 12.3885C8.32634 11.382 9.33729 10.3064 10.284 9.16908C10.6364 8.7316 10.9604 8.27 11.2534 7.78815C11.4853 7.42129 11.6494 7.01561 11.7356 6.59611C11.4732 6.50989 11.2431 6.41676 11.0095 6.36531C10.6226 6.27413 10.2172 6.23765 9.83873 6.12438C9.22376 5.93944 8.61743 5.72458 8.01965 5.49748C7.74128 5.38965 7.66032 5.15041 7.79146 4.84448C7.91876 4.56525 8.06771 4.29588 8.23658 4.03949C8.48766 3.63432 8.75715 3.24136 9.00824 2.83619C9.16058 2.59418 9.25754 2.33715 9.16232 2.07661C8.08585 1.589 6.44759 1.28058 5.43895 1.37146C4.61995 2.5425 3.3006 6.28562 3.23283 7.67726Z" fill="#1B2124"/>
<path d="M3.23283 7.67726C3.76212 8.14229 4.36301 8.51134 5.01541 8.77207C5.53517 8.99183 6.06287 9.20516 6.5786 9.437C6.91597 9.59103 6.97231 9.88387 6.75414 10.2173C5.86267 11.5819 5.13721 13.0338 4.40047 14.4808C4.36827 14.5428 4.37775 14.6243 4.36122 14.7473C4.46952 14.7004 4.57454 14.643 4.67489 14.5761C5.53682 13.847 6.43193 13.1703 7.25812 12.3885C8.32634 11.382 9.33729 10.3064 10.284 9.16908C10.6364 8.7316 10.9604 8.27 11.2534 7.78815C11.4853 7.42129 11.6494 7.01561 11.7356 6.59611C11.4732 6.50989 11.2431 6.41676 11.0095 6.36531C10.6226 6.27413 10.2172 6.23765 9.83873 6.12438C9.22376 5.93944 8.61743 5.72458 8.01965 5.49748C7.74128 5.38965 7.66032 5.15041 7.79146 4.84448C7.91876 4.56525 8.06771 4.29588 8.23658 4.03949C8.48766 3.63432 8.75715 3.24136 9.00824 2.83619C9.16058 2.59418 9.25754 2.33715 9.16232 2.07661C8.08585 1.589 6.44759 1.28058 5.43895 1.37146C4.61995 2.5425 3.3006 6.28562 3.23283 7.67726Z" fill="#1B2124"/>
<path d="M4.08836 8.17385C3.63368 7.8523 3.42005 7.30576 3.55493 6.74978C3.95126 5.11605 5.03024 2.45023 5.65564 1.55577C6.5932 1.47193 8.10737 1.75709 9.10715 2.21236C9.19634 2.45051 8.91122 2.99164 8.77146 3.22001C8.53865 3.59577 8.2888 3.96021 8.056 4.33598C7.89943 4.57376 7.76133 4.82358 7.6433 5.08254C7.5233 5.36656 7.59598 5.588 7.85487 5.68815C8.40821 5.90225 8.97039 6.10152 9.54147 6.26955C9.89234 6.3746 10.2683 6.40843 10.627 6.49299C11.0267 6.58746 11.2509 7.00644 11.0808 7.38748C11.0154 7.53397 10.9393 7.67616 10.8531 7.81257C10.5817 8.25577 10.2819 8.68031 9.95623 9.08266C9.07859 10.1365 8.1415 11.1331 7.15138 12.0658C6.38447 12.7944 5.83489 13.465 5.03812 14.1415C4.94508 14.2036 4.84771 14.2568 4.7473 14.3003C4.76262 14.1863 4.75383 14.1107 4.78369 14.0531C5.46359 12.7106 6.43312 11.3079 7.2549 10.0415C7.45287 9.73608 7.40382 9.46508 7.08784 9.32164C6.61206 9.11347 5.55201 8.92627 5.06772 8.72202C4.72739 8.58571 4.39841 8.39311 4.08836 8.17385Z" fill="url(#paint0_linear_14970_11999)"/>
<defs>
<linearGradient id="paint0_linear_14970_11999" x1="8.59385" y1="2.24494" x2="6.34243" y2="14.5952" gradientUnits="userSpaceOnUse">
<stop stop-color="#FEDA8D"/>
<stop offset="1" stop-color="#CEA653"/>
</linearGradient>
</defs>
</svg>
<span class="flash-sale-discount__text">${discountPercent}% <span class="flash-sale-discount__off">OFF</span></span></span></span>`;
      } else {
        discountContainerHTML = `<span class="discount-container" role="group" style=""><span class="card_product_price_percentoff"> ${discountPercent}% OFF </span></span>`;
      }
    }

    let ratingHTML = "";
    const reviewsCount = numberOfReviews != null && numberOfReviews !== "" ? String(numberOfReviews) : "0";
    // Match Liquid: only show review block when average_rating >= 1.00 (has_review)
    const ratingNum = averageRating != null && averageRating !== "" ? parseFloat(averageRating) : NaN;
    const hasReview = !isNaN(ratingNum) && ratingNum >= 1;
    if (hasReview) {
      ratingHTML = `
        <div class="custom-reviews-main-svg flex">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
            <g clip-path="url(#clip0_1892_4881)"><rect width="16" height="16" rx="4.57143" fill="#099250"/>
            <g filter="url(#filter0_f_1892_4881)"><circle cx="7.99944" cy="7.99993" r="5.71429" fill="white" fill-opacity="0.62"/></g>
            <g clip-path="url(#clip1_1892_4881)"><path d="M8.59197 3.52553L9.69387 5.71601C9.7318 5.79218 9.78768 5.858 9.8567 5.90777C9.92572 5.95755 10.0058 5.98979 10.0901 6.00172L12.5529 6.3522C12.649 6.36438 12.7397 6.4035 12.8144 6.46503C12.8892 6.52656 12.9451 6.60799 12.9756 6.69991C13.006 6.79183 13.0099 6.8905 12.9867 6.98452C12.9635 7.07854 12.9141 7.16408 12.8443 7.23124L11.0615 8.93029C11.0007 8.988 10.955 9.05983 10.9286 9.13941C10.9022 9.21899 10.8958 9.30385 10.9101 9.38648L11.3329 11.7912C11.3478 11.8872 11.3358 11.9854 11.2981 12.0749C11.2605 12.1644 11.1987 12.2416 11.1196 12.2981C11.0406 12.3545 10.9474 12.3878 10.8506 12.3943C10.7537 12.4009 10.6569 12.3803 10.571 12.3351L8.3653 11.1998C8.28956 11.161 8.20566 11.1407 8.12054 11.1407C8.03541 11.1407 7.95151 11.161 7.87578 11.1998L5.67292 12.3351C5.58703 12.3803 5.49024 12.4009 5.39337 12.3943C5.29649 12.3878 5.20333 12.3545 5.1243 12.2981C5.04527 12.2416 4.98347 12.1644 4.94581 12.0749C4.90815 11.9854 4.8961 11.8872 4.91101 11.7912L5.33292 9.38648C5.34716 9.30385 5.34079 9.21899 5.31438 9.13941C5.28796 9.05983 5.24232 8.988 5.18149 8.93029L3.39673 7.22743C3.32659 7.16043 3.27691 7.07489 3.25347 6.98076C3.23003 6.88663 3.23379 6.78778 3.26431 6.6957C3.29484 6.60363 3.35087 6.52211 3.4259 6.46062C3.50094 6.39914 3.59188 6.36022 3.68816 6.34839L6.15197 6.00172C6.23622 5.98979 6.31631 5.95755 6.38533 5.90777C6.45435 5.858 6.51023 5.79218 6.54816 5.71601L7.64911 3.52553C7.69376 3.43899 7.76136 3.36642 7.84452 3.31575C7.92767 3.26508 8.02316 3.23828 8.12054 3.23828C8.21791 3.23828 8.31341 3.26508 8.39656 3.31575C8.47972 3.36642 8.54732 3.43899 8.59197 3.52553V3.52553Z" fill="white"/></g></g>
            <defs><filter id="filter0_f_1892_4881" x="-9.14342" y="-9.14293" width="34.2868" height="34.2856" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/><feGaussianBlur stdDeviation="5.71429" result="effect1_foregroundBlur_1892_4881"/></filter>
            <clipPath id="clip0_1892_4881"><rect width="16" height="16" rx="4.57143" fill="white"/></clipPath><clipPath id="clip1_1892_4881"><rect width="11.4286" height="11.4286" fill="white" transform="translate(2.28516 2.28564)"/></clipPath></defs></svg>
          <p class="average-number">${averageRating}</p>
          <p class="reviews-number">(${reviewsCount})</p>
        </div>`;
    }

    const priceBlockId = `AVTNuS1hhdDlndXdZa__price_B9YDbD${index > 0 ? "-" + index : ""}`;
    const priceBlockHTML = `<product-price class="text-block text-block--align-left text-left h6 spacing-style" data-block-id="${priceBlockId}" data-product-id="${productId || ""}" style="--color: var(--color-foreground); --padding-block-start: 0px; --padding-block-end: 0px; --padding-inline-start: 0px; --padding-inline-end: 0px; --width: 100%"><div ref="priceContainer" class="price-container-non-pdp${hasShikshaMahotsav ? " price-container-non-pdp--shiksha" : ""}"><div class="flex price-container">${salePriceGroup}${comparePriceGroup}</div>${discountContainerHTML}${ratingHTML}</div></product-price>`;

    const cardId = `product-card-ANldKNE55V3IyL0Yxb__static-product-card${index > 0 ? "-" + index : ""}`;
    const linkId = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
    const dataAttrs =
      ` data-product-id="${productId || ""}"` +
      ` data-product-title="${titleEscaped}"` +
      ` data-product-variants-size="${productVariantsSize}"` +
      ` id="${cardId}"` +
      (productTransition ? ` data-product-transition="${String(productTransition).replace(/"/g, "&quot;")}"` : ` data-product-transition="false"`) +
      (l1CategoryName ? ` data-l1-category-name="${String(l1CategoryName).replace(/"/g, "&quot;")}"` : "") +
      (l2CategoryName ? ` data-l2-category-name="${String(l2CategoryName).replace(/"/g, "&quot;")}"` : "") +
      (l3CategoryName ? ` data-l3-category-name="${String(l3CategoryName).replace(/"/g, "&quot;")}"` : "") +
      (productCategoryId ? ` data-product-category-id="${String(productCategoryId).replace(/"/g, "&quot;")}"` : "") +
      (productSubCategoryId ? ` data-product-sub-category-id="${String(productSubCategoryId).replace(/"/g, "&quot;")}"` : "") +
      (productCategorySlug ? ` data-product-category-slug="${String(productCategorySlug).replace(/"/g, "&quot;")}"` : "") +
      ` data-product-handle="${String(productHandle || "").replace(/"/g, "&quot;")}"` +
      ` data-product-variant-id="${variantId || ""}"` +
      ` data-gtm-price-unit="rupees"` +
      ` data-product-price="${variantPriceRaw != null ? variantPriceRaw : ""}"` +
      ` data-product-compare-at-price="${compareAtPriceRaw}"` +
      ` data-product-available="${variantAvailable ? "true" : "false"}"` +
      ` data-product-inventory-quantity="${inventoryQuantity != null ? inventoryQuantity : ""}"` +
      ` data-product-tags="${String(tagsJoined).replace(/"/g, "&quot;")}"` +
      (ribbonName ? ` data-product-tag="${String(ribbonName).replace(/"/g, "&quot;")}"` : "") +
      ` data-preorder="${preorderValue}"` +
      (discountPercent ? ` data-product-discount-percent="${discountPercent}"` : "");

    const cardGalleryClass = `card-gallery ${cardGalleryId} spacing-style border-style${isOutOfStock ? " card-gallery--out-of-stock" : ""}`;
    const cardGalleryStyle = `--border-width: 1px; --border-style: none; --border-color: rgb(var(--color-border-rgb) / 1); --border-radius: 0px; --padding-block-start: 0px; --padding-block-end: 0px; --padding-inline-start: 0px; --padding-inline-end: 0px; --gallery-aspect-ratio: 1;`;

    const productCardContentStyle = `--border-width: 1px; --border-style: none; --border-color: rgb(var(--color-border-rgb) / 1); --border-radius: 0px; --flex-direction: column; --flex-wrap: nowrap; --flex-wrap-mobile: wrap; --horizontal-alignment: ; --vertical-alignment: ; --vertical-alignment-mobile: ; --padding-block-start: 0px; --padding-block-end: 0px; --padding-inline-start: 0px; --padding-inline-end: 0px; --product-card-gap: 0px; --quick-add-display: ${isOutOfStock ? "none" : "flex"}; --quick-add-mobile-display: ${isOutOfStock ? "none" : "flex"};`;

    const mainTitleStyle = `--padding-block-start: 4px; --padding-block-end: 0px; --padding-inline-start: 0px; --padding-inline-end: 0px; --width: fit-content; --max-width: var(--max-width--body-normal);`;

    return `
      <div class="swiper-slide${index === 0 ? " swiper-slide-active" : index === 1 ? " swiper-slide-next" : ""}" data-product-position="${index + 1}" data-product-id="${productId}"${l1CategoryName ? ` data-l1-category-name="${String(l1CategoryName).replace(/"/g, "&quot;")}"` : ""}${l2CategoryName ? ` data-l2-category-name="${String(l2CategoryName).replace(/"/g, "&quot;")}"` : ""}${l3CategoryName ? ` data-l3-category-name="${String(l3CategoryName).replace(/"/g, "&quot;")}"` : ""} role="group" aria-label="${index + 1} / ${totalCount}">
        <div class="resource-list__item${digitalBookTagText ? " pw-has-digital-book" : ""}">
          <product-card class="product-card${hasShikshaMahotsav ? " product-card--desc-shiksha" : ""}${hasFlashSale ? " product-card--flash-sale" : ""}"${dataAttrs}>
            <a id="${linkId}" href="${variantUrl}" class="product-card__link" ref="productCardLink">
              <span class="visually-hidden"> ${titleEscaped} </span>
            </a>
            <div class="product-card__content layout-panel-flex layout-panel-flex--column product-grid__card spacing-style border-style gap-style" style="${productCardContentStyle}">
              <div ref="cardGallery" class="${cardGalleryClass}" style="${cardGalleryStyle}" data-product-id="${productId}" data-image-ratio="square">
                ${galleryInnerHTML}
                ${outOfStockHTML}
                <div class="product-badges__badge">${ribbonHTML}</div>
                ${ctaHTML}
              </div>
              ${digitalBookHTML}
              <div class="product-grid-view-zoom-out--details">
                <h3 class="h4">${(title || "").replace(/</g, "&lt;").replace(/>/g, "&gt;")}</h3>
              </div>
              ${urgencyHTML}
              ${editionHTML}
              ${ageTagHTML}
              <a class="contents user-select-text" ref="productTitleLink" href="${variantUrl}">
                <div class="main-title-container" style="${mainTitleStyle}">
                  <p>${(title || "").replace(/</g, "&lt;").replace(/>/g, "&gt;")}</p>
                </div>
              </a>
              ${priceBlockHTML}
            </div>
          </product-card>
        </div>
      </div>`;
  }

  /**
   * Normalize GraphQL product node + product JSON into data for buildProductCardHTML.
   * @param {Object} product - GraphQL product node
   * @param {number} index - 0-based index
   * @param {Object} context - { sectionId, quickAddColorScheme, decodeId, getProductJson, templateSuffix }
   * @returns {Promise<Object>} data for buildProductCardHTML
   */
  function normalizeProductData(product, index, context) {
    const decodeIdFn = context.decodeId;
    const getProductJson = context.getProductJson;
    const sectionId = context.sectionId;
    const quickAddColorScheme = context.quickAddColorScheme || "";

    const variantEdges = product.variants && product.variants.edges ? product.variants.edges : [];
    let variant = null;
    for (let v = 0; v < variantEdges.length; v++) {
      if (variantEdges[v].node && variantEdges[v].node.availableForSale) {
        variant = variantEdges[v].node;
        break;
      }
    }
    if (!variant && variantEdges[0]) variant = variantEdges[0].node;
    const productId = decodeIdFn(product.id);
    const variantId = variant ? decodeIdFn(variant.id) : "";
    const isDigitalBook = variant ? variant.requiresShipping === false : false;
    const variantTitleMeta = variant && variant.variantTitle && variant.variantTitle.value ? variant.variantTitle.value : null;
    const productHandle = product.handle;
    const productUrl = "/products/" + productHandle;

    const variantPriceSource = variant && variant.price ? variant.price : product.priceRange.minVariantPrice;
    const variantPriceNum = parseFloat(variantPriceSource.amount);
    const price = Number.isFinite(variantPriceNum) ? variantPriceNum.toFixed(2) : "";
    const variantPriceRaw = variantPriceNum;
    const currency = variantPriceSource.currencyCode;
    const currencySymbol = currency === "INR" ? "₹" : currency;

    const compareAtPriceVal = variant
      ? variant.compareAtPrice
        ? variant.compareAtPrice.amount
        : 0
      : product.compareAtPriceRange.minVariantPrice.amount;
    const compareAtPrice = parseFloat(compareAtPriceVal);
    const compareAtPriceRaw = compareAtPrice > 0 ? compareAtPrice : 0;
    const isOnSale = compareAtPrice > parseFloat(price);
    const formattedCompareAtPrice = isOnSale ? formatPrice(compareAtPrice) : null;
    let discountPercent = 0;
    if (isOnSale) discountPercent = Math.round(((compareAtPrice - parseFloat(price)) / compareAtPrice) * 100);

    const featuredImage = (variant && variant.image) || product.featuredImage;
    const imageUrl = featuredImage ? featuredImage.url : "";
    const imageAlt = featuredImage ? featuredImage.altText || product.title : product.title;
    const imageWidth = featuredImage ? featuredImage.width : 300;
    const imageHeight = featuredImage ? featuredImage.height : 300;

    const mediaArray = [];
    if (product.media && product.media.edges && product.media.edges.length > 0) {
      for (let m = 0; m < product.media.edges.length; m++) {
        const mediaNode = product.media.edges[m].node;
        if (mediaNode && mediaNode.previewImage && mediaNode.previewImage.url) {
          mediaArray.push({
            url: mediaNode.previewImage.url,
            alt: mediaNode.previewImage.altText || product.title,
            width: mediaNode.previewImage.width || 500,
            height: mediaNode.previewImage.height || 500,
          });
        }
      }
    }
    if (imageUrl) {
      const variantImageKey = String(imageUrl).split("?")[0];
      const variantImageIndex = mediaArray.findIndex(function (media) {
        return String(media.url).split("?")[0] === variantImageKey;
      });
      if (variantImageIndex > 0) {
        const variantMedia = mediaArray.splice(variantImageIndex, 1)[0];
        mediaArray.unshift(variantMedia);
      } else if (variantImageIndex === -1) {
        mediaArray.unshift({ url: imageUrl, alt: imageAlt, width: imageWidth, height: imageHeight });
      }
    }
    if (mediaArray.length === 0 && imageUrl) {
      mediaArray.push({ url: imageUrl, alt: imageAlt, width: imageWidth, height: imageHeight });
    }

    const tags = product.tags || [];
    const tagsLower = tags.map(function (t) {
      return String(t || "").toLowerCase();
    });
    const badges = [];
    // if (tags.indexOf("label:Pre_Order") !== -1) badges.push({ text: "PRE_ORDER", classes: "color-scheme-1" });
    if (tags.indexOf("TRENDING") !== -1) badges.push({ text: "TRENDING", classes: "color-scheme-2" });
    if (tags.indexOf("PRICE_DROP") !== -1) badges.push({ text: "PRICE_DROP", classes: "color-scheme-3" });

    const hasPreOrderTag = tagsLower.indexOf("pre_order") !== -1;

    const hasPwBooksTag =
      tagsLower.indexOf("pw_books") !== -1 ||
      tagsLower.some(function (t) {
        return t.indexOf("pw_books") !== -1;
      });
    const hasFlyBundleTag = tagsLower.indexOf("fly bundles parent product") !== -1;
    const digitalBookTagText = hasPwBooksTag ? (hasFlyBundleTag ? "WITH DIGITAL BOOK" : "DIGITAL BOOK") : "";
    const templateSuffix = String(
      (context && context.templateSuffix) ||
        (document.body && document.body.getAttribute("data-pw-template-suffix")) ||
        ""
    ).toLowerCase();
    const hasShikshaMahotsav =
      window.PW_ENABLE_SHIKSHA_MAHOTSAV_STYLE !== false && templateSuffix === "shiksha-mahotsav";
    const hasFlashSale =
      window.PW_ENABLE_FLASH_SALE_STYLE !== false && templateSuffix === "flash-sale";
    const urgency = resolveUrgencyLabel(tagsLower);

    const editionMetafield = variant && variant.edition && variant.edition.value ? variant.edition.value : null;
    const textMetafield = variant && variant.text && variant.text.value ? variant.text.value : null;
    const editionTag =
      editionMetafield ||
      textMetafield ||
      tags.filter(function (t) {
        return t.indexOf("Edition") !== -1;
      })[0] ||
      "";

    const ribbonFields = product.productRibbons && product.productRibbons.reference && product.productRibbons.reference.fields ? product.productRibbons.reference.fields : [];
    let ribbonName = "";
    let ribbonColor = "#2563eb";
    for (let r = 0; r < ribbonFields.length; r++) {
      if (ribbonFields[r].key === "ribbons_name") ribbonName = ribbonFields[r].value;
      if (ribbonFields[r].key === "ribbons_color") ribbonColor = ribbonFields[r].value;
    }

    let averageRating = null;
    let numberOfReviews = null;
    const judgmeBadge = product.judgemeBadge;
    if (judgmeBadge && judgmeBadge.value) {
      const parsed = parseJudgemeBadge(judgmeBadge.value);
      if (parsed.averageRating != null) averageRating = parsed.averageRating;
      if (parsed.numberOfReviews != null) numberOfReviews = parsed.numberOfReviews;
    }
    if ((averageRating == null || numberOfReviews == null) && product.rating && product.rating.value) {
      try {
        const ratingData = JSON.parse(product.rating.value);
        if (averageRating == null && ratingData.value != null) averageRating = parseFloat(ratingData.value).toFixed(1);
        if (numberOfReviews == null && (ratingData.count != null || ratingData.reviews_count != null || ratingData.number_of_reviews != null)) {
          numberOfReviews = String(ratingData.count != null ? ratingData.count : ratingData.reviews_count != null ? ratingData.reviews_count : ratingData.number_of_reviews);
        }
      } catch (e) {
        if (averageRating == null) averageRating = product.rating.value;
      }
    }
    if (numberOfReviews == null && product.ratingCount && product.ratingCount.value) {
      numberOfReviews = String(product.ratingCount.value).trim();
    }

    const isAvailable = product.availableForSale;
    const isPreOrder = hasPreOrderTag;
    const preorderValue = isPreOrder ? "yes" : "no";
    const hasOutOfStockTag = tagsLower.indexOf("out of stock") !== -1;
    let isOutOfStock = !isAvailable;
    if (hasFlyBundleTag && hasOutOfStockTag) isOutOfStock = true;

    // Fly-bundle direct add-to-cart data
    const flyVariantIds = variant && variant.flyVariantIds && variant.flyVariantIds.value ? variant.flyVariantIds.value : "";
    const flyOfferId = variant && variant.flyOfferId && variant.flyOfferId.value ? variant.flyOfferId.value : "";
    const flyChildDigitalMap = product.flyChildDigitalMap && product.flyChildDigitalMap.value ? product.flyChildDigitalMap.value : "";
    const isFlyBundleAddable = hasFlyBundleTag && !isOutOfStock && !!flyVariantIds && !!flyOfferId;
    const showViewBundle = hasFlyBundleTag && !isFlyBundleAddable;

    const variants = product.variants && product.variants.edges ? product.variants.edges : [];
    let hasMultipleVariants = variants.length > 1;
    let productVariantsSize = variants.length;
    const hasLinkedProducts = hasMetafieldPayload(product, "linkedProducts");
    const hasRelatedSuperbundle = hasMetafieldPayload(product, "relatedSuperbundle");
    // Match quick-add.liquid / deal-day: open Quick Buy when shopper must choose an option
    let needsQuickBuy = hasMultipleVariants || hasLinkedProducts || hasRelatedSuperbundle;
    const variantAvailable = variant ? variant.availableForSale : isAvailable;
    const tagsJoined = Array.isArray(tags) ? tags.join(",") : "";
    // let preorderValue = isPreOrder ? "yes" : "no";
    const l1CategoryName = product.parentName && product.parentName.value ? String(product.parentName.value) : "";
    const l2CategoryName = product.subCatName && product.subCatName.value ? String(product.subCatName.value) : "";
    const l3CategoryName = product.subSubCatName && product.subSubCatName.value ? String(product.subSubCatName.value) : "";
    const productCategoryId = product.categoryId && product.categoryId.value ? String(product.categoryId.value) : "";
    const productSubCategoryId = product.subCategoryId && product.subCategoryId.value ? String(product.subCategoryId.value) : "";
    const productCategorySlug = product.categorySlug && product.categorySlug.value ? String(product.categorySlug.value) : "";
    const ageTag = product.kidYearTag && product.kidYearTag.value ? String(product.kidYearTag.value).trim() : "";
    const productTransition = context.productTransition || "";

    return Promise.resolve(getProductJson ? getProductJson(productHandle) : null).then(function (productJson) {
      const displayPrice = formatPrice(price);
      let inventoryQuantity = normalizeInventoryQuantity(variant && variant.quantityAvailable);
      if (productJson && Array.isArray(productJson.variants)) {
        if (productJson.variants.length > 1) {
          hasMultipleVariants = true;
          productVariantsSize = productJson.variants.length;
          needsQuickBuy = true;
        }
        const matchedVariant =
          productJson.variants.filter(function (v) {
            return v.id && v.id.toString() === variantId;
          })[0] ||
          productJson.variants.filter(function (v) {
            return v.available;
          })[0] ||
          productJson.variants[0];
        if (matchedVariant && inventoryQuantity == null) {
          inventoryQuantity = normalizeInventoryQuantity(matchedVariant.inventory_quantity);
        }
      }
      return {
        productId: productId,
        variantId: variantId,
        productHandle: productHandle,
        productUrl: productUrl,
        title: variantTitleMeta || product.title,
        price: displayPrice,
        currencySymbol: currencySymbol,
        formattedCompareAtPrice: formattedCompareAtPrice,
        isOnSale: isOnSale,
        discountPercent: discountPercent,
        imageUrl: imageUrl,
        imageAlt: imageAlt,
        imageWidth: imageWidth,
        imageHeight: imageHeight,
        mediaArray: mediaArray,
        ribbonName: ribbonName,
        ribbonColor: ribbonColor,
        badges: badges,
        digitalBookTagText: digitalBookTagText,
        hasShikshaMahotsav: hasShikshaMahotsav,
        hasFlashSale: hasFlashSale,
        urgencyLabel: urgency ? urgency.label : "",
        urgencyEmoji: urgency ? urgency.emoji : "",
        ageTag: ageTag,
        isOutOfStock: isOutOfStock,
        showViewBundle: showViewBundle,
        isPreOrder: isPreOrder,
        isAvailable: isAvailable,
        hasMultipleVariants: hasMultipleVariants,
        needsQuickBuy: needsQuickBuy,
        averageRating: averageRating,
        numberOfReviews: numberOfReviews,
        editionTag: editionTag,
        sectionId: sectionId,
        quickAddColorScheme: quickAddColorScheme,
        index: index,
        productVariantsSize: productVariantsSize,
        compareAtPriceRaw: compareAtPriceRaw,
        variantPriceRaw: variantPriceRaw,
        variantAvailable: variantAvailable,
        inventoryQuantity: inventoryQuantity,
        tagsJoined: tagsJoined,
        preorderValue: preorderValue,
        l1CategoryName: l1CategoryName,
        l2CategoryName: l2CategoryName,
        l3CategoryName: l3CategoryName,
        productCategoryId: productCategoryId,
        productSubCategoryId: productSubCategoryId,
        productCategorySlug: productCategorySlug,
        noSwatchSelected: false,
        productTransition: productTransition,
        isDigitalBook: isDigitalBook,
        isFlyBundleAddable: isFlyBundleAddable,
        flyVariantIds: flyVariantIds,
        flyOfferId: flyOfferId,
        flyChildDigitalMap: flyChildDigitalMap,
      };
    });
  }

  /**
   * Build full HTML for all product cards from GraphQL edges.
   * @param {Array} edges - GraphQL collection.products.edges
   * @param {Object} context - { sectionId, quickAddColorScheme, getProductJson }
   * @returns {Promise<string>} HTML string for all slides
   */
  function buildProductCardsHTML(edges, context) {
    context.decodeId = context.decodeId || decodeId;
    const promises = [];
    const totalCount = edges.length;
    for (let i = 0; i < edges.length; i++) {
      (function (idx) {
        promises.push(
          normalizeProductData(edges[idx].node, idx, context).then(function (data) {
            data.totalCount = totalCount;
            return data;
          }),
        );
      })(i);
    }
    return Promise.all(promises).then(function (dataList) {
      let html = "";
      for (let j = 0; j < dataList.length; j++) {
        html += buildProductCardHTML(dataList[j]);
      }
      return html;
    });
  }

  /**
   * Register the fly-bundle document-level click handler (once).
   * Must be called after cards are injected so fly-bundle buttons work
   * even when no Liquid-rendered fly-bundle cards exist on the page.
   */
  function ensureFlyBundleHandler() {
    if (global.__flyBundleQA) return;
    global.__flyBundleQA = true;
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.fly-bundle-add-btn');
      if (!btn || btn.classList.contains('atc-added')) return;
      e.preventDefault();
      e.stopPropagation();

      var vJson = btn.dataset.flyVariantIds;
      var offerId = btn.dataset.flyOfferId;
      if (!vJson || !offerId) return;

      var childData;
      try { childData = JSON.parse(vJson); } catch (x) { return; }

      var bundleName = btn.dataset.flyBundleName || '';
      var digitalMap = null;
      try { digitalMap = JSON.parse(btn.dataset.flyDigitalMap || 'null'); } catch (x) {}

      var rk = Math.random().toString(36).substring(2, 5);
      var offerIdKeyed = offerId + '_' + rk;

      var items = [];
      var keys = Object.keys(childData);
      for (var i = 0; i < keys.length; i++) {
        var vid = parseInt(childData[keys[i]][0]);
        if (!vid) continue;
        var isDigital = (digitalMap && digitalMap.hasOwnProperty(String(vid))) ? digitalMap[String(vid)] === true : false;
        items.push({
          id: vid,
          quantity: 1,
          properties: {
            '_flyBundles:OfferId': offerIdKeyed,
            '_flyBundles:prodQty': 1,
            '_flyBundles:customField': bundleName,
            '_digital_book': isDigital ? 'true' : 'false',
            '_purchase_type': 'null'
          }
        });
      }
      if (!items.length) return;

      btn.classList.add('atc-added');

      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: { data: { source: 'product-form-component', itemCount: 1, isOptimistic: true } }
      }));

      setTimeout(function () {
        btn.classList.remove('atc-added');
      }, 1500);

      fetch('/cart/add.js?app=gfbBundleApp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items })
      })
        .then(function (r) { if (!r.ok) throw new Error(r.status); })
        .catch(function (err) {
          console.error('Fly Bundle quick-add error:', err);
          fetch('/cart.js').then(function (r) { return r.json(); }).then(function (cart) {
            document.dispatchEvent(new CustomEvent('cart:update', {
              bubbles: true,
              detail: { data: { source: 'fly-bundle-quick-add', itemCount: cart.item_count } }
            }));
          }).catch(function () {});
        });
    });
  }

  /**
   * Log the time from page navigation start (window.productCardLoadingTimeStart, set in
   * layout/theme.liquid) until the first product card's first image finishes loading. Logs once
   * per page load, on whichever container reaches this first.
   * @param {Element} container - Element containing rendered product cards (e.g. the grid).
   */
  function trackFirstImageLoadTime(container) {
    if (!container || global.__firstCardImageTimeLogged) return;
    const firstCard = container.querySelector(".swiper-slide, .resource-list__item");
    const img = firstCard ? firstCard.querySelector("img") : null;
    if (!img) return;
    global.__firstCardImageTimeLogged = true;
    const start = typeof global.productCardLoadingTimeStart === "number" ? global.productCardLoadingTimeStart : performance.now();
    const logLoadTime = function () {
      const loadTime = (performance.now() - start) / 1000;
      // console.log(`First product card image load time: ${loadTime.toFixed(3)} seconds`);
      (global.dataLayer = global.dataLayer || []).push({
        event: "ga_pwst_product_card_loaded",
        seconds: loadTime,
        url: global.location.href,
      });
    };
    if (img.complete) {
      logLoadTime();
    } else {
      img.addEventListener("load", logLoadTime, { once: true });
    }
  }

  /**
   * Replace a card's lightweight poster image with its full gallery, cloned in from the inert
   * <template class="card-gallery-template">. Cloning connects the slideshow-component (running
   * its normal upgrade/init) and only then causes its slide images to be requested.
   * @param {Element} card
   */
  function revealCardGallery(card) {
    const template = card.querySelector("template.card-gallery-template");
    if (!template || !(template instanceof HTMLTemplateElement)) return;
    const poster = template.previousElementSibling;
    const clone = template.content.cloneNode(true);
    if (poster && poster.classList.contains("card-gallery-poster")) {
      poster.replaceWith(clone);
      template.remove();
    } else if (template.parentNode) {
      template.parentNode.insertBefore(clone, template);
      template.remove();
    }
  }

  /**
   * Watch product cards inside a container and reveal each card's deferred gallery (extra images +
   * slideshow-component init) only once that card scrolls into view, instead of upfront for every
   * fetched card.
   * @param {Element} container - Element containing rendered product cards.
   * @param {Object} [options]
   * @param {Element|null} [options.root] - Intersection root. Pass the swiper's scroll container
   *   (e.g. .swiper-container) so horizontally clipped, off-screen slides aren't treated as visible.
   * @param {string} [options.rootMargin]
   */
  function initLazyCardGalleries(container, options) {
    if (!container) return;
    options = options || {};
    const cards = container.querySelectorAll(".swiper-slide, .resource-list__item");
    if (typeof IntersectionObserver === "undefined") {
      cards.forEach(revealCardGallery);
      return;
    }
    const observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          revealCardGallery(entry.target);
          observer.unobserve(entry.target);
        });
      },
      { root: options.root || null, rootMargin: options.rootMargin || "200px" }
    );
    cards.forEach(function (card) {
      observer.observe(card);
    });
  }

  global.CustomProductListCards = {
    decodeId: decodeId,
    buildProductCardHTML: buildProductCardHTML,
    normalizeProductData: normalizeProductData,
    buildProductCardsHTML: buildProductCardsHTML,
    ensureFlyBundleHandler: ensureFlyBundleHandler,
    revealCardGallery: revealCardGallery,
    initLazyCardGalleries: initLazyCardGalleries,
    trackFirstImageLoadTime: trackFirstImageLoadTime,
  };
})(typeof window !== "undefined" ? window : this);
